/**
 * @file Encargado de proporcionar la funcionalidad para lanzar las apis que se definan en el json
 * correspondiente al flujo que se está ejecutando.
 * @module Minder.MegaMinder
 */
import {
  all, call, put, select,
} from 'redux-saga/effects';
import { accessPropertyByDotNotation } from '@bitComponents/utils/objectUtils';

/**
 * Función generadora preparada para acceder a una ruta determinada del estado de redux
 * siendo invocadas a través de yield select
 * @param {string} dotPathToMinder
 * @returns Objeto al que se quería acceder del estado o null en caso de no existir
 */
function* getMinderState(dotPathToMinder) {
  return yield select((state) => {
    return accessPropertyByDotNotation(state, dotPathToMinder);
  });
}

/**
 * Función generadora que dispara un api que se le pase como parámetro
 * @param {obj} params Parámetros necesarios para saber que api se tiene que lanzar
*              y las funciones de error. Se necesitan los siguientes campos:
* <pre>
*              - ws: nombre del Saga a ejecutar para iniciar la llamada al api
*              - showError: función para mostrar el stopper, se obtiene del AppController
*              - normalizeHttpErrors: normalizador de los errores de los servicios web
*              - minderSagas Objeto con todos los sagas que se pueden ejecutar desde el minder
* </pre>
 */
function* executeSaga(params) {
  const {
    ws: sagaToExecute,
    showError,
    normalizeHttpErrors,
    minderSagas,
  } = params;
  if (minderSagas && minderSagas[sagaToExecute]) {
    const response = yield call(minderSagas[sagaToExecute], { showError, normalizeHttpErrors });
    return response;
  }
  return null;
}

/**
 * Método que ejecuta los apis que toquen dependiendo del paso que se confirme
 * y de la config del flujo
 * @param {obj} params Parámetros necesarios para ejecutar los apis. Se necesitan los siguientes:
 * <pre>
 *            - payload: aqui viene el step que acaba de confirmar el usuario
 *            - showError: función para mostrar el stopper, se obtiene del AppController
 *            - normalizeHttpErrors: normalizador de los errores de los servicios web
 *            - dotPathToMinder: ruta en formato dotString donde se encuentra la info
 *              para el minder en el stado de redux
 *            - minderSagas Objeto con todos los sagas que se pueden ejecutar desde el minder
 *            - loaderControlActions Acciones de redux para settear el estado de waiting
 * </pre>
 */
export function* executeApisToAdvance(params) {
  const {
    payload,
    showError,
    normalizeHttpErrors,
    dotPathToMinder,
    minderSagas,
    loaderControlActions,
  } = params;
  const minder = yield call(getMinderState, dotPathToMinder);
  yield put(loaderControlActions.setLoaderState({ loading: true }));

  const { confirmedStep } = payload;
  const { stepId } = confirmedStep;
  const confirmedStepFlow = minder.steps[stepId];

  let dismissLoading = true;

  for (let i = 0; i < confirmedStepFlow.ws.length; i += 1) {
    const wsStep = confirmedStepFlow.ws[i];

    // TODO si falla un api lanza el siguiente, deberia parar, o deberiamos al menos hacer que sea configurable (api obligatorio vs opcional)
    const responses = yield all(wsStep.map(ws => call(executeSaga, {
      ws, showError, normalizeHttpErrors, minderSagas,
    })));

    const responseFail = responses.find(response => (response && response.error && !response.continue));
    const responseHalt = responses.find(response => (response && response.halt));

    if (responseFail) {
      break;
    }
    if (responseHalt) {
      dismissLoading=false;
    }

  }
  if(dismissLoading){
    yield put(loaderControlActions.setLoaderState({ loading: false }));
  }
}
